export type ThemeMode = "light" | "dark" | "system" | "sunrise-sunset" | "scheduled";

export type ThemeModeRecord =
  | "LIGHT"
  | "DARK"
  | "SYSTEM"
  | "SUNRISE_SUNSET"
  | "SCHEDULED";

export interface ThemePalette {
  background: string;
  surface: string;
  foreground: string;
  primary: string;
  accent: string;
}

export interface ThemePaletteRecord {
  background?: string;
  surface?: string;
  foreground?: string;
  primary?: string;
  accent?: string;
}

export interface CustomTheme {
  name: string;
  light: ThemePalette;
  dark: ThemePalette;
}

export interface CustomThemeRecord {
  name?: string;
  light?: ThemePaletteRecord;
  dark?: ThemePaletteRecord;
}

export interface ThemePreferences {
  mode: ThemeMode;
  lightTheme: string;
  darkTheme: string;
  customTheme: CustomTheme;
  scheduledLightTime: string;
  scheduledDarkTime: string;
}

export interface ThemePreferencesRecord {
  mode?: ThemeModeRecord;
  lightTheme?: string;
  darkTheme?: string;
  customTheme?: CustomThemeRecord;
  scheduledLightTime?: string;
  scheduledDarkTime?: string;
}

const DEFAULT_LIGHT_PALETTE: ThemePalette = {
  background: "#ffffff",
  surface: "#f7f8fa",
  foreground: "#0f172a",
  primary: "#2563eb",
  accent: "#0ea5e9",
};

const DEFAULT_DARK_PALETTE: ThemePalette = {
  background: "#0f172a",
  surface: "#1e293b",
  foreground: "#e2e8f0",
  primary: "#38bdf8",
  accent: "#a855f7",
};

export const DEFAULT_THEME_PREFERENCES: ThemePreferences = {
  mode: "light",
  lightTheme: "classic",
  darkTheme: "midnight",
  customTheme: {
    name: "custom",
    light: DEFAULT_LIGHT_PALETTE,
    dark: DEFAULT_DARK_PALETTE,
  },
  scheduledLightTime: "07:00",
  scheduledDarkTime: "20:00",
};

const toThemeMode = (mode?: ThemeModeRecord | null): ThemeMode => {
  switch (mode) {
    case "DARK":
      return "dark";
    case "SYSTEM":
      return "system";
    case "SUNRISE_SUNSET":
      return "sunrise-sunset";
    case "SCHEDULED":
      return "scheduled";
    case "LIGHT":
    default:
      return "light";
  }
};

const toThemeModeRecord = (mode: ThemeMode): ThemeModeRecord => {
  switch (mode) {
    case "dark":
      return "DARK";
    case "system":
      return "SYSTEM";
    case "sunrise-sunset":
      return "SUNRISE_SUNSET";
    case "scheduled":
      return "SCHEDULED";
    case "light":
    default:
      return "LIGHT";
  }
};

const normalizePaletteRecord = (
  record: ThemePaletteRecord | null | undefined,
  fallback: ThemePalette
): ThemePalette => {
  const legacy = record as ThemePaletteRecord & { muted?: string };
  return {
    background: record?.background ?? fallback.background,
    surface: record?.surface ?? legacy?.muted ?? fallback.surface,
    foreground: record?.foreground ?? fallback.foreground,
    primary: record?.primary ?? fallback.primary,
    accent: record?.accent ?? fallback.accent,
  };
};

export const fromApiThemePreferences = (
  record?: ThemePreferencesRecord | null
): ThemePreferences => {
  if (!record) {
    return DEFAULT_THEME_PREFERENCES;
  }

  const custom = record.customTheme ?? {};
  const light = custom.light ?? {};
  const dark = custom.dark ?? {};
  const lightPalette = normalizePaletteRecord(
    light,
    DEFAULT_THEME_PREFERENCES.customTheme.light
  );
  const darkPalette = normalizePaletteRecord(
    dark,
    DEFAULT_THEME_PREFERENCES.customTheme.dark
  );

  return {
    ...DEFAULT_THEME_PREFERENCES,
    mode: toThemeMode(record.mode),
    lightTheme: record.lightTheme ?? DEFAULT_THEME_PREFERENCES.lightTheme,
    darkTheme: record.darkTheme ?? DEFAULT_THEME_PREFERENCES.darkTheme,
    customTheme: {
      name: custom.name ?? DEFAULT_THEME_PREFERENCES.customTheme.name,
      light: lightPalette,
      dark: darkPalette,
    },
    scheduledLightTime:
      record.scheduledLightTime ?? DEFAULT_THEME_PREFERENCES.scheduledLightTime,
    scheduledDarkTime:
      record.scheduledDarkTime ?? DEFAULT_THEME_PREFERENCES.scheduledDarkTime,
  };
};

export const toApiThemePreferences = (
  preferences: ThemePreferences
): ThemePreferencesRecord => ({
  mode: toThemeModeRecord(preferences.mode),
  lightTheme: preferences.lightTheme,
  darkTheme: preferences.darkTheme,
  customTheme: {
    name: preferences.customTheme?.name ?? DEFAULT_THEME_PREFERENCES.customTheme.name,
    light: preferences.customTheme?.light ?? DEFAULT_THEME_PREFERENCES.customTheme.light,
    dark: preferences.customTheme?.dark ?? DEFAULT_THEME_PREFERENCES.customTheme.dark,
  },
  scheduledLightTime: preferences.scheduledLightTime,
  scheduledDarkTime: preferences.scheduledDarkTime,
});
